import React, { useState, useEffect } from 'react';
import { StepProps } from '../types/stepTypes';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOnboardingDataRedux } from '../../hooks/useOnboardingDataRedux';
import { validatePassword, validateEmail } from '../../utils/passwordUtils';

const SignupStep: React.FC<StepProps> = ({ 
  currentStep, 
  setCurrentStep, 
  completedSteps, 
  setCompletedSteps,
  setUserEmail
}) => {
  const { updateData, nextStep, signupData, data: onboardingData, restoreFromBackup } = useOnboardingDataRedux();
  
  // Use local state for form inputs - always start empty
  const [email, setEmailLocal] = useState('');
  const [password, setPasswordLocal] = useState('');
  const [fullName, setFullNameLocal] = useState('');
  const [acceptedPolicy, setAcceptedPolicyLocal] = useState(false);

  // Force clear form when component mounts or step changes
  useEffect(() => {
    if (currentStep === 1) {
      console.log('🧹 Clearing signup form to ensure empty state');
      setEmailLocal('');
      setPasswordLocal('');
      setFullNameLocal('');
      setAcceptedPolicyLocal(false);
      
      // Additional clearing after a delay to override browser autofill
      const timer = setTimeout(() => {
        console.log('🧹 Secondary clear to override browser autofill');
        setEmailLocal('');
        setPasswordLocal('');
        setFullNameLocal('');
        setAcceptedPolicyLocal(false);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [currentStep]);
  
  // No auto-restore functionality - always start with empty form
  
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Helper functions to update local state and Redux state
  const setEmail = (value: string) => {
    setEmailLocal(value);
    updateData({ email: value });
  };
  const setPassword = (value: string) => {
    setPasswordLocal(value);
    updateData({ password: value });
  };
  const setFullName = (value: string) => {
    setFullNameLocal(value);
    updateData({ fullName: value });
  };
  const setAcceptedPolicy = (value: boolean) => {
    setAcceptedPolicyLocal(value);
    updateData({ acceptedPolicy: value });
  };
  
  const location = useLocation();
  const navigate = useNavigate();

  

  if (currentStep !== 1) return null;

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isLoading) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      // Validate full name
      if (!fullName.trim()) {
        setError('Please enter your full name');
        setIsLoading(false);
        return;
      }
      
      if (fullName.trim().length < 2) {
        setError('Full name must be at least 2 characters long');
        setIsLoading(false);
        return;
      }
      

      
      // Validate email
      const emailValidation = validateEmail(email);
      if (!emailValidation.isValid) {
        setError(emailValidation.error || 'Please enter a valid email address');
        setIsLoading(false);
        return;
      }
      
      // Validate password
      const passwordValidation = await validatePassword(password);
      if (!passwordValidation.isValid) {
        setError(passwordValidation.errors[0]); // Show first error
        setIsLoading(false);
        return;
      }
      
      // Check if user has accepted the policy - CRITICAL VALIDATION
      if (!acceptedPolicy) {
        setError('You must accept our Terms of Service and Privacy Policy before creating an account.');
        setIsLoading(false);
        return;
      }
      
      // Save the email for later use
      setUserEmail?.(email);
      
      // Data is already saved in Redux through real-time updates
      // Use the proper nextStep function to advance
      console.log('Moving to next step with data:', { email, password, fullName, acceptedPolicy });
      nextStep({
        email,
        password,
        fullName,
        acceptedPolicy,
      });
      
      // Also update local state for immediate UI response
      const newCompletedSteps = [...completedSteps];
      newCompletedSteps[0] = true;
      console.log('Setting completed steps:', newCompletedSteps);
      console.log('Setting current step to:', 2);
      setCompletedSteps(newCompletedSteps);
      setCurrentStep?.(2);
      
      setIsLoading(false);
      
    } catch (err: any) {
      console.error('Validation error:', err);
      setError('Failed to validate credentials. Please try again.');
      setIsLoading(false);
    }
  };

  // Google signup will be handled in the completion step with actual Clerk integration
  const handleGoogleSignUp = async () => {
    // For now, just show an error since we'll handle this in completion step
    setError('Google signup will be available in the final step');
  };



  return (
    <div className="w-full max-w-sm md:max-w-md mx-auto">
      <div className="text-center mb-6 md:mb-8">
        <h2 className="text-xl md:text-2xl font-bold text-black mb-2" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
          Create Your Account
        </h2>
        <p className="text-gray-600">Start your mental health journey with us</p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-red-700 font-medium">
              {error}
            </p>
          </div>
        </div>
      )}

      <div className="space-y-4 md:space-y-6">
        <form onSubmit={handleEmailSignUp} className="space-y-4" autoComplete="off">
          {/* Hidden honeypot fields to confuse browser autofill */}
          <div style={{ display: 'none' }}>
            <input type="text" name="fake_username" autoComplete="username" />
            <input type="password" name="fake_password" autoComplete="current-password" />
          </div>
          <div>
            <label htmlFor="signup-fullname-field" className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <input
              id="signup-fullname-field"
              name="signup-fullname-unique"
              type="text"
              value={fullName}
              onChange={(e) => {
                const newFullName = e.target.value;
                setFullName(newFullName);
                if (error) setError('');
              }}
              className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg 
                         focus:ring-2 focus:ring-gray-400 focus:border-gray-400 
                         transition-all duration-200 bg-white hover:border-gray-400
                         disabled:bg-gray-50 disabled:text-gray-500"
              placeholder="John Doe"
              disabled={isLoading}
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor="signup-email-field" className="block text-sm font-medium text-gray-700 mb-2">
              Email address
            </label>
            <input
              id="signup-email-field"
              type="email"
              value={email}
              onChange={(e) => {
                const newEmail = e.target.value;
                setEmail(newEmail);
                if (error) setError('');
              }}
              className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg 
                         focus:ring-2 focus:ring-gray-400 focus:border-gray-400 
                         transition-all duration-200 bg-white hover:border-gray-400
                         disabled:bg-gray-50 disabled:text-gray-500"
              placeholder="you@example.com"
              disabled={isLoading}
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor="signup-password-field" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              id="signup-password-field"
              name="signup-password-unique"
              type="password"
              value={password}
              onChange={(e) => {
                const newPassword = e.target.value;
                setPassword(newPassword);
                if (error) setError('');
              }}
              className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg 
                         focus:ring-2 focus:ring-gray-400 focus:border-gray-400 
                         transition-all duration-200 bg-white hover:border-gray-400
                         disabled:bg-gray-50 disabled:text-gray-500"
              placeholder="••••••••"
              disabled={isLoading}
              autoComplete="off"
            />
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-2">Must be at least 8 characters with uppercase, lowercase, and numbers</p>
              {password && (
                <div className="space-y-1">
                  <div className={`text-xs flex items-center gap-2 ${password.length >= 8 ? 'text-gray-700' : 'text-gray-400'}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${password.length >= 8 ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                    At least 8 characters
                  </div>
                  <div className={`text-xs flex items-center gap-2 ${/[A-Z]/.test(password) ? 'text-gray-700' : 'text-gray-400'}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${/[A-Z]/.test(password) ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                    Uppercase letter
                  </div>
                  <div className={`text-xs flex items-center gap-2 ${/[a-z]/.test(password) ? 'text-gray-700' : 'text-gray-400'}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${/[a-z]/.test(password) ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                    Lowercase letter
                  </div>
                  <div className={`text-xs flex items-center gap-2 ${/\d/.test(password) ? 'text-gray-700' : 'text-gray-400'}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${/\d/.test(password) ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                    Number
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Policy Acceptance Checkbox */}
          <div className="flex items-start space-x-3">
            <input
              id="accept-policy"
              type="checkbox"
              checked={acceptedPolicy}
              onChange={(e) => {
                const newAcceptedPolicy = e.target.checked;
                setAcceptedPolicy(newAcceptedPolicy);
                if (error) setError('');
              }}
              className="mt-1 h-4 w-4 text-gray-800 border-gray-300 rounded 
                         focus:ring-gray-400 focus:ring-2 cursor-pointer"
            />
            <label htmlFor="accept-policy" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
              I agree to the{' '}
              <a 
                href="/terms?from=signup&step=1" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-black hover:text-gray-700 underline font-medium transition-colors duration-200"
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a 
                href="/privacy?from=signup&step=1" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-black hover:text-gray-700 underline font-medium transition-colors duration-200"
              >
                Privacy Policy
              </a>
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-black text-white font-medium py-3 px-4 rounded-lg
                       hover:bg-gray-800 focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 
                       transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                       shadow-sm hover:shadow-md transform hover:scale-[1.02]"
          >
            {isLoading ? 'Validating...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignupStep; 